import { useState, type FormEvent } from 'react';
import { requestPasswordReset } from '../../utils/auth';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ForgotPasswordModal({ isOpen, onClose, onSuccess }: ForgotPasswordModalProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      await requestPasswordReset(email);
      setIsSuccess(true);
      setEmail('');
      setTimeout(() => {
        onSuccess();
        onClose();
        setIsSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao solicitar recuperação');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError(null);
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-[rgba(2,6,23,0.95)] backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="relative max-w-[420px] w-full bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-6 border border-[#1e293b] shadow-[0_20px_60px_rgba(0,0,0,0.9)]">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-transparent border-none text-[#64748b] text-2xl cursor-pointer hover:text-[#e5e7eb] hover:bg-[rgba(148,163,184,0.1)] transition-all"
          aria-label="Fechar"
        >
          ×
        </button>

        <h3 className="text-2xl font-bold text-[#f1f5f9] mb-1 mt-0">Recuperar palavra-passe</h3>
        <p className="text-[0.9rem] text-[#cbd5e1] mb-5">
          Introduza o seu e-mail e enviaremos um link para redefinir a sua palavra-passe.
        </p>

        {isSuccess ? (
          <div className="bg-green-500/10 border border-green-500/50 rounded-lg px-4 py-3 text-sm text-green-400">
            <p className="font-medium mb-1">E-mail enviado com sucesso!</p>
            <p>Verifique a sua caixa de entrada para o link de recuperação.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg px-3 py-2 text-sm text-red-400">
                {error}
              </div>
            )}
            
            <div>
              <label htmlFor="forgot-email" className="block text-[0.85rem] font-medium text-[#cbd5e1] mb-1.5">
                E-mail
              </label>
              <input
                type="email"
                id="forgot-email"
                placeholder="o-seu-email@exemplo.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-[#334155] px-3.5 py-2.5 text-[0.95rem] bg-[#0f172a] text-[#f1f5f9] placeholder:text-[#64748b] outline-none focus:border-[#8b5cf6] focus:ring-2 focus:ring-[rgba(139,92,246,0.2)] transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full inline-flex items-center justify-center rounded-xl px-4 py-3 text-[0.95rem] font-semibold bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-white shadow-[0_10px_30px_rgba(139,92,246,0.4)] hover:shadow-[0_15px_40px_rgba(139,92,246,0.5)] hover:translate-y-[-2px] transition-all mt-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isLoading ? 'A enviar...' : 'Enviar link de recuperação'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
